import { router } from "@inertiajs/react";
import { useState } from "react";
import {
  AlertTriangle,
  FileCode,
  GitBranch,
  ExternalLink,
} from "lucide-react";
import { SiGithub } from "@icons-pack/react-simple-icons";
import Layout from "../Layout";
import { BreadcrumbNavigation } from "../../components/BreadcrumbNavigation";
import { PageHeader } from "../../components/PageHeader";
import { IconRow } from "../../components/IconRow";
import { TerraformStateRow } from "../../components/TerraformStateRow";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import { NotificationSettingsSection } from "../../components/NotificationSettingsSection";
import { SectionCard } from "../../components/SectionCard";
import { EmptyState } from "../../components/ui/EmptyState";

interface Workspace {
  name: string;
  slug: string;
}

interface NotificationSetting {
  id: number;
  platform: string;
  destination: string;
  metadata: {
    channel_name: string;
  };
}

interface TerraformState {
  id: number;
  backend_type: "s3" | "github";
  s3_bucket_name: string | null;
  s3_region: string | null;
  s3_key: string | null;
  github_state_filename: string | null;
  aws_account: {
    id: number;
    uuid: string;
    display_name: string;
  };
}

interface GithubRepositoryEditProps {
  workspace: Workspace;
  github_repository: {
    uuid: string;
    owner: string;
    name: string;
    branch: string;
    created_at: string;
  };
  can_edit: boolean;
  can_delete: boolean;
  notification_settings: NotificationSetting[];
  integrations: {
    slack_connected: boolean;
  };
  terraform_states: TerraformState[];
}

export default function GithubRepositoryEdit({
  workspace,
  github_repository,
  can_edit,
  can_delete,
  notification_settings,
  integrations,
  terraform_states,
}: GithubRepositoryEditProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const breadcrumbs = [
    { label: "Workspaces", href: "/workspaces" },
    { label: workspace.name, href: `/workspaces/${workspace.slug}` },
    { label: `${github_repository.owner}/${github_repository.name}`, href: `/workspaces/${workspace.slug}/github_repositories/${github_repository.uuid}` },
    { label: "Settings" },
  ];

  const handleDelete = () => {
    router.delete(
      `/workspaces/${workspace.slug}/github_repositories/${github_repository.uuid}`,
      {
        onBefore: () => setIsDeleting(true),
        onFinish: () => {
          setIsDeleting(false);
          setIsDeleteModalOpen(false);
        },
      }
    );
  };

  return (
    <Layout title={`Settings - ${github_repository.owner}/${github_repository.name}`}>
      <section className="px-4 sm:px-6 lg:px-8 py-16 pt-40">
        <div className="max-w-4xl mx-auto">
          <BreadcrumbNavigation items={breadcrumbs} />

          <PageHeader
            title={`${github_repository.owner}/${github_repository.name} settings`}
          />

          {/* Repository info */}
          <SectionCard title="Repository" icon={SiGithub}>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              <IconRow icon={ExternalLink}>
                <a
                  href={`https://github.com/${github_repository.owner}/${github_repository.name}`}
                  className="hover:text-primary transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {github_repository.owner}/{github_repository.name}
                </a>
              </IconRow>
              <IconRow icon={GitBranch}>
                <span className="font-mono">{github_repository.branch}</span>
              </IconRow>
            </div>
          </SectionCard>

          {/* Infrastructure states section */}
          <SectionCard
            title="Infrastructure states"
            icon={FileCode}
            className="mt-6"
          >
            {terraform_states.length === 0 ? (
              <EmptyState
                icon={FileCode}
                title="No infrastructure states connected"
                description="Infrastructure states are created and managed from the cloud account settings pages."
              />
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {terraform_states.map((state) => (
                  <TerraformStateRow
                    key={state.id}
                    state={state}
                    connectedTo={{
                      label: state.aws_account.display_name,
                      href: `/workspaces/${workspace.slug}/aws_accounts/${state.aws_account.uuid}`,
                    }}
                  />
                ))}
              </div>
            )}
          </SectionCard>

          <NotificationSettingsSection
            workspaceSlug={workspace.slug}
            githubRepositoryUuid={github_repository.uuid}
            notificationSettings={notification_settings}
            integrations={integrations}
            canEdit={can_edit}
          />

          {can_delete && (
            <SectionCard title="Danger zone" icon={AlertTriangle} variant="danger" className="mt-6">
              <div className="flex items-center justify-between py-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Delete repository
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    Permanently remove this repository configuration and all associated recommendations.
                  </p>
                </div>
                <button
                  onClick={() => setIsDeleteModalOpen(true)}
                  className="flex-shrink-0 ml-4 inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-600 text-xs font-medium rounded-full text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </SectionCard>
          )}
        </div>
      </section>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Git Repository"
        message={`Are you sure you want to delete "${github_repository.owner}/${github_repository.name}"? This will permanently remove all recommendations and cannot be undone.`}
        confirmText="Delete Repository"
        variant="danger"
        isLoading={isDeleting}
      />
    </Layout>
  );
}
